import React, { useState, useEffect } from "react";
import axios from "axios";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

export const CreateGoal = ({ onGoalCreated }) => {
  const [goalType, setGoalType] = useState("test"); // "test" or "skill"
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [syllabusData, setSyllabusData] = useState([]);
  const [goalDetails, setGoalDetails] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const getDefaultTestDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };
  const [testDate, setTestDate] = useState(getDefaultTestDate());
  const [reminder, setReminder] = useState("1 Day Before");
  const [goalCreated, setGoalCreated] = useState(false);
  const [error, setError] = useState("");

  // Helper to get today's date in YYYY-MM-DD format for min attribute
  const getTodayDate = () => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  };

  // Fetch syllabus data from API (single call for both subjects and chapters)
  useEffect(() => {
    const fetchSyllabus = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${baseUrl}/api/v2/syllabus`, {});
        const data = response.data || [];

        setSyllabusData(data);

        // Extract unique subjects
        const uniqueSubjects = [...new Set(data.map((item) => item.subject))];
        setSubjects(uniqueSubjects);

        // Auto-select the first subject if available
        if (uniqueSubjects.length > 0) {
          setSelectedSubject(uniqueSubjects[0]);
        }
      } catch (error) {
        console.error("Error fetching syllabus:", error);
        setError("Failed to load syllabus data");
        setSyllabusData([]);
        setSubjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSyllabus();
  }, [baseUrl]);

  // Update topics/chapters when subject changes
  useEffect(() => {
    if (selectedSubject && syllabusData.length > 0) {
      // Find chapters for the selected subject
      const subjectData = syllabusData.find(
        (item) => item.subject === selectedSubject
      );
      if (subjectData && subjectData.chapters) {
        // Map chapters to topics format (keeping the structure for compatibility)
        const topicsData = subjectData.chapters.map((chapter) => ({
          name: chapter.name,
        }));
        setTopics(topicsData);

        // Auto-select the first topic if available
        if (topicsData.length > 0) {
          setSelectedTopic(topicsData[0].name);
        } else {
          setSelectedTopic("");
        }
      } else {
        setTopics([]);
        setSelectedTopic("");
      }
    } else {
      setTopics([]);
      setSelectedTopic("");
    }
  }, [selectedSubject, syllabusData]);

  // Effect to update goal details based on subject and topic
  useEffect(() => {
    if (selectedSubject && selectedTopic) {
      setGoalDetails(`${selectedTopic} from ${selectedSubject}`);
    } else {
      setGoalDetails(""); // Clear if subject/topic is not selected
    }
  }, [selectedSubject, selectedTopic]);

  const handleSubjectChange = (e) => {
    const newSubject = e.target.value;
    setSelectedSubject(newSubject);
    // Reset topic when subject changes - topic will be auto-selected in the useEffect above
    setSelectedTopic("");
  };

  const handleCreateGoal = async () => {
    setError(""); // Clear previous errors

    // Field validation
    if (!selectedSubject) {
      console.log("Setting error: Please select a subject."); // Temporary log
      setError("Please select a subject.");
      setTimeout(() => setError(""), 2000); // Toast disappears after 10 seconds
      return;
    }
    if (!selectedTopic) {
      console.log("Setting error: Please select a topic."); // Temporary log
      setError("Please select a topic.");
      setTimeout(() => setError(""), 2000); // Toast disappears after 10 seconds
      return;
    }
    if (!goalDetails) {
      console.log("Setting error: Please add goal details."); // Temporary log
      setError("Please add goal details.");
      setTimeout(() => setError(""), 2000); // Toast disappears after 10 seconds
      return;
    }
    if (goalType === "test") {
      if (!testDate) {
        console.log("Setting error: Please select a test date."); // Temporary log
        setError("Please select a test date.");
        setTimeout(() => setError(""), 2000); // Toast disappears after 10 seconds
        return;
      }
      if (!reminder) {
        console.log("Setting error: Please set a reminder."); // Temporary log
        setError("Please set a reminder.");
        setTimeout(() => setError(""), 2000); // Toast disappears after 10 seconds
        return;
      }
    }

    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      } else if (userId) {
        headers["X-User-ID"] = userId;
      } else {
        console.log("Setting error: Authentication required."); // Temporary log
        setError("Authentication required to create a goal.");
        setTimeout(() => setError(""), 2000); // Toast disappears after 10 seconds
        return;
      }

      const goalData = {
        subject: selectedSubject,
        topic: selectedTopic,
        details: goalDetails,
        testDate: goalType === "test" ? testDate : undefined,
        reminder: goalType === "test" ? reminder : undefined,
        isSkillImprovement: goalType === "skill",
        goal_type: goalType === "test" ? "Self_improvement" : "Skill_updation",
      };

      const response = await axios.post(
        `${baseUrl}/v2/create-goals`,
        goalData,
        {
          headers,
        }
      );

      if (response.data?.message === "Goal created successfully") {
        setGoalCreated(true);
        setError("");

        // Clear cached goals data so fresh data is fetched when navigating to My Goals
        window.goalsData = null;

        // Reset to first subject and topic
        if (subjects.length > 0) {
          setSelectedSubject(subjects[0]);
        }
        setGoalDetails("");
        setTestDate(getDefaultTestDate());
        setReminder("1 Day Before");
        setTimeout(() => {
          setGoalCreated(false);
          // Call the callback to navigate back to MyGoals and refresh data
          if (onGoalCreated) {
            onGoalCreated();
          }
        }, 1500); // Toast disappears after 2 seconds
      } else {
        console.log("Setting error: Failed to create goal from API."); // Temporary log
        setError(response.data?.message || "Failed to create goal.");
        setTimeout(() => setError(""), 2000); // Toast disappears after 10 seconds
      }
    } catch (err) {
      console.error("Error creating goal:", err);
      console.log("Setting error: Catch block error."); // Temporary log
      setError("Failed to create goal. Please try again.");
      setTimeout(() => setError(""), 2000); // Toast disappears after 10 seconds
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 mx-auto">
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-600">Loading subjects...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4  mx-auto">
      {/* Subject and Topic */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <p className="text-gray-800 mb-2">Select a Subject</p>
          <select
            value={selectedSubject}
            onChange={handleSubjectChange}
            className="w-full border rounded-md p-3 text-gray-800"
          >
            <option value="" disabled>
              Select a Subject
            </option>
            {subjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <p className="text-gray-800 mb-2">Select a Topic</p>
          <select
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            disabled={!selectedSubject}
          >
            <option value="" disabled>
              {selectedSubject ? "Select a Topic" : "Select a Subject first"}
            </option>
            {topics.map((topic) => (
              <option key={topic.name} value={topic.name}>
                {topic.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Goal Details */}
      <div className="mb-6">
        <p className="text-gray-800 mb-2">Goal Details</p>
        <input
          type="text"
          value={goalDetails}
          onChange={(e) => setGoalDetails(e.target.value)}
          className="w-full border border-gray-300 rounded-3xl p-3 text-gray-800"
          placeholder="Add details about your goal"
        />
      </div>

      {/* Test-specific fields */}
      {goalType === "test" && (
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1">
            <p className="text-gray-800 mb-2">Test Date</p>
            <input
              type="date"
              value={testDate}
              onChange={(e) => setTestDate(e.target.value)}
              min={getTodayDate()} // Disable past dates
              className="w-full border border-gray-300 rounded-3xl p-2 text-gray-700"
            />
          </div>
          <div className="flex-1">
            <p className="text-gray-800 mb-2">Set Reminder</p>
            <select
              value={reminder}
              onChange={(e) => setReminder(e.target.value)}
              className="w-full border border-gray-800 rounded-md p-3 text-gray-700"
            >
              <option value="" disabled>
                Select Reminder
              </option>
              <option value="1 Week Before">1 Week Before</option>
              <option value="3 Days Before">3 Days Before</option>
              <option value="1 Day Before">1 Day Before</option>
              <option value="On the Day">On the Day</option>
            </select>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-center">
        <button
          onClick={handleCreateGoal}
          className="bg-[#0D99FF] hover:bg-purple-800 text-white font-semibold py-3 px-12 rounded-full"
        >
          Create Goal
        </button>
      </div>

      {/* Feedback Alerts */}
      {goalCreated && (
        <div className="fixed top-4 right-4 bg-green-200 text-green-800 py-2 px-4 rounded-md shadow-md z-50">
          Congrats, Goal created successful.
          <br />
        </div>
      )}

      {error && (
        <div className="fixed top-4 right-4 bg-yellow-200 text-yellow-800 py-2 px-4 rounded-md shadow-md z-50">
          {error}
        </div>
      )}
    </div>
  );
};

export default CreateGoal;
